import { ref } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import apiClient from '@/plugins/store/actions/api/ApiClient';
import { i18n } from '@/plugins/internationalization/i18n';
import { downloadBlob } from '@/util/downloadFile';

export type DownloadTarget = {
    /** The url exactly as the backend wrote it - i.e. what the <a href> would otherwise have opened. */
    url: string;
    /** Set when the file lives behind our own api: fetched through ApiClient so the request is authenticated. */
    route?: string | null;
    /** Explicit filename from the backend. Wins over everything we can derive. */
    fileName?: string | null;
};

type FetchedFile = { blob: Blob; contentDisposition: unknown; sourceUrl: string };

const DEFAULT_FILE_NAME = 'download';
const FRIENDLY_API_BASE_URL = `${process.env.VUE_APP_API_SERVER_URL || ''}`.replace(/\/+$/, '');
const FILENAME_EXTENDED_PATTERN = /filename\*\s*=\s*[^']*''([^;]+)/i;
const FILENAME_QUOTED_PATTERN = /filename\s*=\s*"([^"]+)"/i;
const FILENAME_PLAIN_PATTERN = /filename\s*=\s*([^;]+)/i;
const PATH_SEPARATOR_PATTERN = /[\\/]/;
const LEADING_DOTS_PATTERN = /^\.+/;
/* The characters Windows refuses to save, plus the control characters a crafted header could smuggle in. */
// eslint-disable-next-line no-control-regex
const ILLEGAL_FILE_NAME_CHARACTERS = /[<>:"|?*\u0000-\u001f]/g;

/* Keeps a header or url segment from smuggling a path out of the downloads folder, and drops the characters Windows refuses to save. */
function sanitizeFileName(fileName: string): string {
    const baseName = fileName.split(PATH_SEPARATOR_PATTERN).pop() || '';

    return baseName.replace(ILLEGAL_FILE_NAME_CHARACTERS, '_').replace(LEADING_DOTS_PATTERN, '').trim();
}

function normalizePathname(pathname: string): string {
    return pathname.replace(/\/+$/, '') || '/';
}

/* Cross-origin this only ever sees a value if the host sends Access-Control-Expose-Headers: Content-Disposition. */
export function fileNameFromContentDisposition(header: unknown): string {
    if (typeof header !== 'string') {
        return '';
    }

    const extendedMatch = header.match(FILENAME_EXTENDED_PATTERN);
    if (extendedMatch) {
        try {
            return sanitizeFileName(decodeURIComponent(extendedMatch[1].trim()));
        } catch (error) {
            /* Malformed percent-encoding: fall through to the plain forms below. */
        }
    }

    const quotedMatch = header.match(FILENAME_QUOTED_PATTERN);
    if (quotedMatch) {
        return sanitizeFileName(quotedMatch[1]);
    }

    const plainMatch = header.match(FILENAME_PLAIN_PATTERN);

    return plainMatch ? sanitizeFileName(plainMatch[1]) : '';
}

export function fileNameFromUrl(url: string): string {
    try {
        const { pathname } = new URL(url, window.location.origin);

        return sanitizeFileName(decodeURIComponent(pathname.split('/').pop() || ''));
    } catch (error) {
        return '';
    }
}

export function shouldFetchDownloadTarget(target: DownloadTarget): boolean {
    if (target.route) {
        return true;
    }

    if (!FRIENDLY_API_BASE_URL) {
        return false;
    }

    try {
        const sourceUrl = new URL(target.url, window.location.origin);
        const friendlyApiUrl = new URL(FRIENDLY_API_BASE_URL, window.location.origin);
        const sourcePathname = normalizePathname(sourceUrl.pathname);
        const friendlyApiPathname = normalizePathname(friendlyApiUrl.pathname);

        return sourceUrl.origin === friendlyApiUrl.origin && (friendlyApiPathname === '/' || sourcePathname === friendlyApiPathname || sourcePathname.startsWith(`${friendlyApiPathname}/`));
    } catch (error) {
        return false;
    }
}

async function fetchFile(target: DownloadTarget): Promise<FetchedFile | null> {
    if (target.route) {
        /* Our own api: ApiClient owns the base url, the subscription key and the bearer token. skipHandleError makes it
           rethrow the 4xx/5xx it would otherwise swallow, so one failure produces exactly one toast - ours. */
        const response = await apiClient.send('get', target.route, { returnResponse: true, responseType: 'blob', skipHandleError: true });

        if (!response?.data) {
            return null;
        }

        return {
            blob: new Blob([response.data], { type: response.headers['content-type'] }),
            contentDisposition: response.headers['content-disposition'],
            sourceUrl: target.route,
        };
    }

    /* Deliberately a bare axios call rather than ApiClient: this url points at a host we do not control, and ApiClient
       would both prefix VUE_APP_API_SERVER_URL and attach our bearer token and subscription key to it. Download mode
       still requires that host to allow CORS for the app origin; CrownData is configured as that friendly api. No extra
       headers and no withCredentials, so the request stays a cors-simple GET that needs no preflight - keep it that way. */
    if (!shouldFetchDownloadTarget(target)) {
        return null;
    }

    const sourceUrl = new URL(target.url, window.location.origin).toString();
    const response = await axios.get(sourceUrl, { responseType: 'blob' });

    return {
        blob: response.data instanceof Blob ? response.data : new Blob([response.data], { type: response.headers['content-type'] }),
        contentDisposition: response.headers['content-disposition'],
        sourceUrl,
    };
}

/**
 * Fetches a url as a blob and hands it to the browser as a download.
 *
 * The html `download` attribute is not an option here: browsers silently ignore it cross-origin, which is exactly the
 * case this exists for - the api and our blob storage are both on a different origin than the app.
 */
export function useDownloadUrl() {
    const isDownloading = ref(false);
    const toast = useToast();

    async function downloadUrl(target: DownloadTarget): Promise<void> {
        if (isDownloading.value) {
            return;
        }

        isDownloading.value = true;

        try {
            const file = await fetchFile(target);

            if (!file) {
                /* ApiClient already toasted (a 403, or a 401 whose refresh failed) and there is nothing to save. */
                return;
            }

            const fileName = sanitizeFileName(target.fileName || '') || fileNameFromContentDisposition(file.contentDisposition) || fileNameFromUrl(file.sourceUrl) || DEFAULT_FILE_NAME;

            downloadBlob(file.blob, fileName);
        } catch (error) {
            toast.error(i18n.global.t('global.errors.unknown'));
        } finally {
            isDownloading.value = false;
        }
    }

    return {
        downloadUrl,
        isDownloading,
    };
}
